// moduleDataBridge.js — capability-scoped data access for workspace MODULES
// (manifest v2). Generalization of extensionDataBridge.js for the module
// plane: instead of an extension's declared-entity list, the gate is the
// module's server-granted `capabilities` (workspace.settings.workspaceModule
// .capabilities — data, written by the workspace owner/admin, never by the
// module itself).
//
// The loader (workspaceProjects.js) registers the built bridge as the
// `moduleData` context function when a capability grant is present, so module
// functions reach data via `el.call('moduleData', from, op, args)` — never
// through raw fetch / el.sdk (the publish-time module audit forbids those).
//
// Client-side this is defense-in-depth ONLY — the server enforces the same
// grant on every route it guards; this bridge just fails fast + keeps honest
// modules honest:
//   1. Only data ops (list/get/create/update/upsert/remove + subscribe).
//   2. Every entity is FORCED into the `workspaceProject.*` namespace — a
//      module can never reach organization.*, admin.*, auth.*, payment.*, ….
//      (`orgIntegration` + `recordCollections`/`fieldDefs` below are the
//      three deliberate, narrowly-scoped exceptions — see each block.)
//   3. The generic `records` store is collection-gated: args.collection must
//      be in capabilities.records.collections, ops in capabilities.records.ops.
//   4. Entities outside the grant are rejected.
//
// ── `orgIntegration` + `recordCollections`/`fieldDefs` (tickets/workspace.md
// "module-plane `integrations` capability — make 'Data' an installable
// catalog app", DECIDED 2026-08-06, Nika: "BUILD IT, server-authoritative —
// same trust model as the aiFunctions and records decisions") ─────────────
//   - `orgIntegration` / op 'call' ONLY, row-addressed (`args.idOrSlug`
//     required — never the body-addressed `/call` route, which has no
//     module-grant escape hatch server-side). Requires
//     `capabilities.integrations = { kinds, capabilities }` on the grant;
//     `args.capability` must be in the granted capability set (or the grant's
//     `capabilities` is the wildcard `'*'`). The KIND itself is checked
//     SERVER-SIDE only (OrgIntegrationsController.callByIdOrSlug +
//     AccessResolver.isIntegrationCallGranted) — the client can't know a row's
//     kind from `idOrSlug` alone without a round trip, so this bridge is
//     defense-in-depth on the capability name only; the server is the actual
//     authority, exactly like every other entity here.
//   - `recordCollections` / `fieldDefs`, ops 'list'/'get' ONLY (read-only
//     schema metadata, never row data) — gated on the module holding ANY
//     `capabilities.records` grant at all (not a specific collection; these
//     two entities are already `requireWorkspaceMember`-readable server-side
//     for every member regardless of the records collection allowlist — see
//     server/src/domains/recordCollections + fieldDefs routes — so this bridge
//     only needs to establish "this module IS a records-capable module",
//     letting a Data-catalog app enumerate collections/fields it wasn't
//     statically granted row access to, per the ticket's proposed shape).
//     A `capabilities.records.collections` array containing the literal
//     string `'*'` is also accepted as "records-capable" here — NOTE this is
//     ONLY a schema-enumeration signal for these two meta entities; it does
//     NOT (yet) unlock row data for arbitrary collections on the `records`
//     entity below — the server's `resolveGrantedRecordCollections` has no
//     wildcard support for actual row access, so granting it here would be a
//     client-side promise the server can't keep. A full row-data wildcard is
//     explicitly left as follow-up work, not silently half-built.
//
// ── `analyzed` — READ-ONLY site analytics for TRUSTED modules (Shaker
// program, CONTRACT §8: a BI app draws visits → view_item → add_to_cart →
// begin_checkout → purchase). The fourth deliberate exception to the
// `workspaceProject.*` namespace, and the narrowest one:
//     el.call('moduleData', 'analyzed', 'totals',     { since?, tz?, projectId?, excludeProjectId? })
//     el.call('moduleData', 'analyzed', 'funnel',     { steps, since?, until?, tz?, projectId?, excludeProjectId? })
//     el.call('moduleData', 'analyzed', 'listEvents', { message, since?, projectId?, excludeProjectId?, limit?, offset?, order? })
//   → sdk.execute('analyzed', 'totals' | 'funnel' | 'listEvents', { filter[, options] }).
// The BRIDGE sets the scope, never the module:
//   - the CALLER must be an installed module of this workspace whose ref
//     tier is 'trusted' (moduleIdOf(this.context) → settings.workspaceModule
//     or a settings.workspaceApps[] entry). An unidentified caller — shell
//     code, a read-only verb's proxied context, a sandboxed or uninstalled
//     module — is refused;
//   - `workspaceId` is the TAB's workspace, always. A module that names any
//     other workspace is refused, never silently re-pointed: the server
//     honours an explicit workspaceId for every workspace the member belongs
//     to, so a forwarded id would read another tenant's visitors;
//   - `family` is 'projects', always — visitors of the workspace's published
//     projects. The team's own shell telemetry ('workspace' family: who used
//     the shell, which pages) is not a module's to read;
//   - listEvents needs one exact `message` (a named event such as
//     'purchase') and reads info captures only (logType 'log') — never a
//     site's console lines or bug reports;
//   - only the filter keys named above reach the SDK, with plain values; any
//     other op (ingest, ingestPublic, sessions, users, bugs, pages, …) and any
//     other key is refused.
// The server applies its own member gate on top (the workspace pair the
// member may read; projectId narrows inside it), so this entry only narrows.
// It never caches: a period switch re-reads, and reads are rare (one per
// board mount + period). Inside a verb the manifest marks `readOnly`
// (moduleReadOnlyCall.js) it is refused like every read that is not a record
// or schema read — that caller's context is a proxy, so its module cannot be
// named.

// ── `assistants` — the site-assistant settings of the CALLER's projects, for
// a TRUSTED module that holds `capabilities.assistants === true` (the
// system/assistants-app: a small dash for the AI assistant of a Symbols
// project, Nika 2026-10-22: "make it configurable from workspace"). The fifth
// deliberate exception to the `workspaceProject.*` namespace:
//     el.call('moduleData', 'assistants', op, { id, ...rest })
//       op: list | get | update | revisions | rollback | usage | resetCounter | testChat
//   → sdk.execute('assistants', op, args).
// The gate here is narrow on purpose:
//   - the CALLER must be an installed module of this workspace whose ref tier
//     is 'trusted' (moduleIdOf(this.context), as `analyzed` reads it) AND whose
//     own ref grants `capabilities.assistants === true` (data on the workspace
//     record, written by the owner/admin — never by the module). Another app's
//     grant never counts: the check reads the caller's ref, not the union;
//   - only the eight ops above reach the SDK; any other op is refused.
// The bridge does NOT scope the data: an assistant belongs to a Symbols
// PROJECT, not to the tab's workspace. The backend (the Fly control Worker
// behind the SDK service) checks the signed-in Symbols user and that user's
// role on the assistant's project on every call — a viewer reads, an owner /
// admin writes — so no workspace id is added and none is honoured. Inside a
// read-only verb (moduleReadOnlyCall.js) it is refused like every non-record
// read.

// ── `growth` — the Symbols sign-up funnel (fly docs/FUNNEL.md §3), READ-ONLY,
// for the Growth app (system/growth-app, Nika 2026-10-23: "make a new one into
// the appstore and also chain it to system org so we see the analytics in our
// workspace"). The sixth deliberate exception to the `workspaceProject.*`
// namespace:
//     el.call('moduleData', 'growth', 'funnel',   { since?, until?, tz?, groupBy? })
//     el.call('moduleData', 'growth', 'accounts', { stage?, since?, until?, tz?,
//                                                   limit?, offset?, order?, sort? })
//   → sdk.execute('growth', 'funnel',   { filter })
//   → sdk.execute('growth', 'accounts', { filter, options })   (GrowthService)
// The gate is narrow on purpose:
//   - the CALLER must be an installed module of this workspace whose ref tier
//     is 'trusted' AND whose owner is `system` — a curated catalog app, the
//     rule the install flow (shared workspaceModuleRef.js) and the server
//     (WorkspaceAppManifest deriveAssistantsCapability) already apply to the
//     `ai` / `assistants` asks. A customer's own same-org module is trusted
//     too and is refused here: a Symbols-team member who opens that workspace
//     must not hand that module our funnel or our accounts list;
//   - only the two reads reach the SDK, with the named keys only and plain
//     values (`limit` capped); any other op or key is refused. The manifest
//     asks for no capability: the app holds no records grant, no AI grant.
// The bridge does NOT scope the data: the funnel belongs to the Symbols system
// org, not to the tab's workspace. The server answers a member of the system
// org only (requireSymbolsSystemOrg) and refuses everyone else with 403, which
// the app shows as "for the Symbols team". No workspace id is added and none
// is honoured. It never caches (a range switch re-reads). Inside a read-only
// verb (moduleReadOnlyCall.js) it is refused like every non-record read.

import _wsShared from '@symbo-ls/workspace-shared'
import { isReadOnlyDataCall, readOnlyCallOf, refuseInReadOnlyCall } from './moduleReadOnlyCall.js'
import { modulePrefsCall } from './modulePrefs.js'
const shellGetSDK = (...args) => _wsShared.functions.getSDK(...args)
const recordsEntryKey = (...args) => _wsShared.functions.recordsEntryKey(...args)
// Data ops a module may invoke. Everything else (rpc, auth, custom procs) is
// denied. Mirrors the extension bridge's verb set the sdk adapter supports.
const ALLOWED_OPS = new Set(['list', 'get', 'create', 'update', 'upsert', 'remove', 'subscribe'])

// BOOT-RESHUFFLE F6 (records stampede): a module board mounts N widgets that
// each issue the SAME records read at the same tick — measured 13×
// /core/workspaces/<ws>/records in one boot, saturating the browser's 6
// HTTP/1.1 connections to the dev API and queueing everything else behind
// them. Identical READS (list/get, promise-style) within a short window
// collapse onto one in-flight request; writes invalidate their collection's
// window so a re-list after a mutation always sees the new row. Keyed by
// workspace scope so a switch can never serve the previous tenant's rows.
const _READ_TTL_MS = 5000
const _readCache = new Map() // key → { at, promise }
const _readKey = (root, entity, operation, args) => {
  try {
    const ws = String(
      root?.workspace?.id ?? root?.workspace?._id ?? root?.jwtClaims?.active_workspace_id ?? ''
    )
    return ws + '|' + entity + '|' + operation + '|' + JSON.stringify(args || {})
  } catch (_) {
    return null // unkeyable → no dedupe
  }
}
const _sweepReadCache = () => {
  if (_readCache.size < 200) return
  const cutoff = Date.now() - _READ_TTL_MS
  for (const [k, v] of _readCache) if (v.at < cutoff) _readCache.delete(k)
}

// Strip the namespace if the caller already wrote one, so 'workspaceProject.x',
// 'x', and a stray 'public.x' all normalize to the bare entity name we gate on.
const _bareEntity = (from) => {
  const s = String(from || '').trim()
  if (!s) return ''
  const dot = s.lastIndexOf('.')
  return (dot >= 0 ? s.slice(dot + 1) : s).toLowerCase()
}

// Read-only ops the recordCollections/fieldDefs meta entities permit from a
// module — never create/update/remove (schema mutation stays owner/admin-only
// through the admin UI's direct SDK path, never the module bridge).
const READONLY_META_OPS = new Set(['list', 'get'])

// Best-effort workspace id off the live root — same lookup `_readKey` above
// uses for the records dedupe cache; reused here so orgIntegration.call +
// the meta reads default `workspaceId` without every module having to thread
// it through by hand.
const _rootWsId = (root) =>
  (root && root.workspace && (root.workspace.id || root.workspace._id)) || undefined

// Org scope for orgIntegration dispatch — same resolution chain the calendar
// bridge uses. The sdk's callOrgIntegrationCapability REQUIRES orgId
// (sdk@3.14.677 enforces it client-side); a module must never supply its own
// org, so the bridge stamps it from root state (caught live on dev
// 2026-08-07: every Track B search threw "orgId is required").
const _rootOrgId = (root) =>
  root?.jwtClaims?.workspace_org_id || (root?.org && (root.org.id || root.org._id)) || undefined

// ── analyzed (see the header) ──────────────────────────────────────────────
// Module op (lower-cased) → the SDK op and the filter keys a module may send.
const ANALYZED_READS = {
  totals: { op: 'totals', keys: ['since', 'tz', 'projectId', 'excludeProjectId'] },
  funnel: {
    op: 'funnel',
    keys: ['steps', 'since', 'until', 'tz', 'projectId', 'excludeProjectId']
  },
  listevents: { op: 'listEvents', keys: ['message', 'since', 'projectId', 'excludeProjectId'] }
}
// Keys the bridge owns. A module may send them only with the value the bridge
// sets anyway (the tab's workspace, the projects family, info captures).
const ANALYZED_OWNED_KEYS = ['workspaceId', 'family', 'logType']
const ANALYZED_EVENT_OPTIONS = ['limit', 'offset', 'order']
// The server's own default page for /events — a module never asks for more.
const ANALYZED_EVENTS_MAX_LIMIT = 500
// The server's exact-match ceiling for `message` (CONTRACT §8, CORE-C).
const ANALYZED_MESSAGE_MAX = 64
const ANALYZED_STRING_MAX = 200

const _analyzedRefusal = (message) =>
  Promise.reject(
    Object.assign(new Error('[module] ' + message), { code: 'module_analytics_refused' })
  )

// The installed ref of a module id ('owner/key', the id moduleIdOf returns)
// on THIS workspace: the takeover module or an additive app. Extension refs
// never match — they run sandboxed, outside the realm.
const _installedModuleRef = (root, moduleId) => {
  const id = String(moduleId || '').toLowerCase()
  const settings = root && root.workspace && root.workspace.settings
  if (!id || !settings || typeof settings !== 'object') return null
  const refs = []
  if (settings.workspaceModule && typeof settings.workspaceModule === 'object') {
    refs.push(settings.workspaceModule)
  }
  if (Array.isArray(settings.workspaceApps)) refs.push(...settings.workspaceApps)
  for (const ref of refs) {
    if (!ref || typeof ref !== 'object' || (ref.type && ref.type !== 'project')) continue
    if (!ref.owner || !ref.key) continue
    if ((String(ref.owner) + '/' + String(ref.key)).toLowerCase() === id) return ref
  }
  return null
}

// One filter value as the SDK sends it, or undefined when it is not plain.
const _analyzedValue = (key, value) => {
  if (key === 'steps') {
    if (typeof value === 'string') return value.trim() ? value.trim() : undefined
    if (!Array.isArray(value) || !value.length || value.length > 8) return undefined
    return value.every((v) => typeof v === 'string' && v.trim())
      ? value.map((v) => v.trim())
      : undefined
  }
  if ((key === 'since' || key === 'until') && value instanceof Date) {
    return isNaN(value.getTime()) ? undefined : value.toISOString()
  }
  if (typeof value !== 'string') return undefined
  const v = value.trim()
  if (!v) return undefined
  if (key === 'message') return v.length <= ANALYZED_MESSAGE_MAX ? v : undefined
  return v.length <= ANALYZED_STRING_MAX ? v : undefined
}

// `{ filter, options }` for one analyzed read, or `{ error }`.
const _analyzedRequest = (read, args, wsId) => {
  const a = args && typeof args === 'object' && !Array.isArray(args) ? args : {}
  const allowed = new Set([...read.keys, ...ANALYZED_OWNED_KEYS])
  if (read.op === 'listEvents') ANALYZED_EVENT_OPTIONS.forEach((k) => allowed.add(k))
  for (const k of Object.keys(a)) {
    if (!allowed.has(k)) return { error: `analytics filter "${k}" is not permitted from a module.` }
  }
  if (a.workspaceId != null && String(a.workspaceId) !== String(wsId)) {
    return { error: 'analytics reads stay in the workspace this tab shows.' }
  }
  if (a.family != null && a.family !== 'projects') {
    return {
      error: "analytics reads cover the workspace's published projects only (family 'projects')."
    }
  }
  if (a.logType != null && a.logType !== 'log') {
    return { error: "analytics events are the info captures only (logType 'log')." }
  }
  const filter = {}
  for (const k of read.keys) {
    if (a[k] == null) continue
    const v = _analyzedValue(k, a[k])
    if (v === undefined)
      return { error: `analytics filter "${k}" has a value a module may not send.` }
    filter[k] = v
  }
  if (read.op === 'funnel' && !filter.steps) {
    return { error: 'analytics funnel needs `steps` (event names).' }
  }
  filter.workspaceId = String(wsId)
  filter.family = 'projects'
  if (read.op !== 'listEvents') return { filter }
  if (!filter.message) {
    return { error: 'analytics events need one exact `message` (a named event).' }
  }
  filter.logType = 'log'
  const options = {}
  if (a.limit != null) {
    const n = Number(a.limit)
    if (!Number.isInteger(n) || n < 1)
      return { error: 'analytics events `limit` must be 1 or more.' }
    options.limit = Math.min(n, ANALYZED_EVENTS_MAX_LIMIT)
  }
  if (a.offset != null) {
    const n = Number(a.offset)
    if (!Number.isInteger(n) || n < 0)
      return { error: 'analytics events `offset` must be 0 or more.' }
    options.offset = n
  }
  if (a.order != null) {
    if (a.order !== 'asc' && a.order !== 'desc') {
      return { error: "analytics events `order` is 'asc' or 'desc'." }
    }
    options.order = a.order
  }
  return { filter, options }
}

// ── assistants (see the header) ────────────────────────────────────────────
// Module op (lower-cased) → the SDK op. Nothing else reaches the SDK.
const ASSISTANTS_OPS = {
  list: 'list',
  get: 'get',
  update: 'update',
  revisions: 'revisions',
  rollback: 'rollback',
  usage: 'usage',
  resetcounter: 'resetCounter',
  testchat: 'testChat'
}

const _assistantsRefusal = (message) =>
  Promise.reject(
    Object.assign(new Error('[module] ' + message), { code: 'module_assistants_refused' })
  )

// ── growth (see the header) ────────────────────────────────────────────────
// Module op (lower-cased) → the SDK op, the filter keys and the paging keys a
// module may send. Nothing else reaches the SDK.
const GROWTH_READS = {
  funnel: { op: 'funnel', keys: ['since', 'until', 'tz', 'groupBy'], options: [] },
  accounts: {
    op: 'accounts',
    keys: ['stage', 'since', 'until', 'tz'],
    options: ['limit', 'offset', 'order', 'sort']
  }
}
// The curated catalog owner: the platform's one publisher of App Store apps
// (WORKSPACE_APPS.md "Curation v1"), the same literal the install flow and
// the server's grant derivation compare against.
const GROWTH_CATALOG_OWNER = 'system'
const GROWTH_ACCOUNTS_MAX_LIMIT = 200
// A date, an IANA zone or a vocabulary word — none is longer.
const GROWTH_STRING_MAX = 64

const _growthRefusal = (message) =>
  Promise.reject(Object.assign(new Error('[module] ' + message), { code: 'module_growth_refused' }))

// One growth value as the SDK sends it, or undefined when it is not plain.
const _growthValue = (key, value) => {
  if (key === 'limit' || key === 'offset') {
    const n = Number(value)
    if (!Number.isInteger(n) || n < (key === 'limit' ? 1 : 0)) return undefined
    return key === 'limit' ? Math.min(n, GROWTH_ACCOUNTS_MAX_LIMIT) : n
  }
  if (key === 'order') return value === 'asc' || value === 'desc' ? value : undefined
  if ((key === 'since' || key === 'until') && value instanceof Date) {
    return isNaN(value.getTime()) ? undefined : value.toISOString()
  }
  if (typeof value !== 'string') return undefined
  const v = value.trim()
  return v && v.length <= GROWTH_STRING_MAX ? v : undefined
}

// `{ args }` for one growth read (`{ filter }`, plus `options` for accounts),
// or `{ error }`.
const _growthRequest = (read, args) => {
  const a = args && typeof args === 'object' && !Array.isArray(args) ? args : {}
  const named = [...read.keys, ...read.options]
  for (const k of Object.keys(a)) {
    if (!named.includes(k)) return { error: `growth filter "${k}" is not permitted from a module.` }
  }
  const filter = {}
  const options = {}
  for (const k of named) {
    if (a[k] == null) continue
    const v = _growthValue(k, a[k])
    if (v === undefined) return { error: `growth filter "${k}" has a value a module may not send.` }
    if (read.options.includes(k)) options[k] = v
    else filter[k] = v
  }
  return { args: read.op === 'accounts' ? { filter, options } : { filter } }
}

// Build the scoped data facade for one module's capability grant. Returns a
// `this`-bound function suitable for context.functions registration
// (el.call('moduleData', from, op, args)). `getSdkFn` is injectable for tests;
// defaults to the shared getSDK(). `moduleIdOf(context)` names the installed
// module that owns a scope context (workspaceProjects.js moduleIdOfContext) —
// the `prefs` entity needs it to find the caller's own bag; without it every
// prefs call is refused.
export const buildModuleDataBridge = (
  root,
  capabilities = {},
  { getSdkFn = shellGetSDK, moduleIdOf = null } = {}
) => {
  let recordsCap = (capabilities && capabilities.records) || null
  // COLD-BOOT RACE FALLBACK: the bridge used to freeze the grant at build
  // time. When the loader's pass ran before the workspace settings bag
  // resolved, `capabilities` arrived EMPTY and every records call failed
  // '[module] records collection "…" is not in this module's capability
  // grant' for the rest of the session — data never loaded (reported live
  // on bellforge, 2026-08-13). When the SNAPSHOT grants nothing, re-read
  // the live server ref (workspace.settings.workspaceModule — the grant is
  // DATA on that record) so a late-resolving grant heals the same bridge.
  // A non-empty snapshot keeps build-time semantics untouched (additive
  // apps pass their own per-app grant — never widened by the takeover ref).
  // A collections entry is a string OR `{ key, read, write }` (per-collection
  // access levels, F1). This bridge gates MEMBERSHIP only — read/write levels
  // are enforced server-side per caller (AccessResolver), so both shapes
  // collapse to their key here. String-coercing an object entry would turn it
  // into '[object Object]' and reject every call on that collection.
  const _fillSets = (cap, cols, ops) => {
    for (const c of Array.isArray(cap && cap.collections) ? cap.collections : []) {
      const k = recordsEntryKey(c).toLowerCase()
      if (k) cols.add(k)
    }
    const src = Array.isArray(cap && cap.ops) && cap.ops.length ? cap.ops : [...ALLOWED_OPS]
    for (const o of src) ops.add(String(o || '').toLowerCase())
  }
  const recordCollections = new Set()
  const recordOps = new Set()
  _fillSets(recordsCap, recordCollections, recordOps)
  // Live-grant sync — call before any membership guard, on EVERY call. The
  // bridge used to FREEZE the grant at build time; when the loader's pass ran
  // before the workspace settings bag resolved, the snapshot arrived EMPTY and
  // every records call failed '[module] records collection "…" is not in this
  // module's capability grant' for the rest of the session — data never
  // loaded (reported live on bellforge, 2026-08-13). The first fix healed
  // only while the set was still EMPTY, which left the sibling shape open: a
  // NON-EMPTY snapshot taken before a stored grant gained a collection kept
  // rejecting exactly that collection for the whole session — measured on
  // bellforge 2026-08-16 (Nika, live): 23 collections loaded, `bf_profiles`
  // alone failed with this message although the stored grant on the workspace
  // record carried it (audited: 24/24 present); the banner's Retry re-ran the
  // loader through the same frozen bridge, so only a hard reload cleared it.
  //
  // Now the membership set is the UNION of the build-time snapshot and the
  // LIVE grants on the workspace record — the takeover ref
  // (settings.workspaceModule) AND every additive ref (settings.workspaceApps[])
  // — re-read per call. That is the same rule the server applies
  // (AccessResolver.resolveGrantedRecordCollections unions the same two
  // sources), so the client gate can no longer be NARROWER than the server's
  // for a session that outlives a grant write; it never widens beyond what the
  // record grants, and per-collection read/write levels stay server-enforced
  // per caller. Sets only grow (mutated in place); the read is a few small
  // arrays, so per-call cost is negligible. Never throws — a torn-down root
  // during a workspace switch just leaves the sets as they were.
  const _syncLiveGrant = () => {
    try {
      const ws = root && root.workspace
      const settings = ws && ws.settings
      if (!settings) return
      const refs = [settings.workspaceModule]
      if (Array.isArray(settings.workspaceApps)) refs.push(...settings.workspaceApps)
      for (const ref of refs) {
        const live = (ref && ref.capabilities && ref.capabilities.records) || null
        if (live && Array.isArray(live.collections) && live.collections.length) {
          _fillSets(live, recordCollections, recordOps)
        }
      }
    } catch (_) {
      /* sets stay as they were — the guard rejects exactly as before */
    }
  }
  // "Is this module records-capable at all" — gates recordCollections/
  // fieldDefs meta reads (see file header). Any non-empty grant qualifies,
  // including a literal `'*'` entry (schema-enumeration signal only — see
  // header note on why that never widens actual row-data access above).

  const integrationsCap = (capabilities && capabilities.integrations) || null
  const grantedIntegrationCapabilities =
    integrationsCap && integrationsCap.capabilities === '*'
      ? '*'
      : new Set(
          (Array.isArray(integrationsCap && integrationsCap.capabilities)
            ? integrationsCap.capabilities
            : []
          ).map((c) => String(c || '').toLowerCase())
        )
  const hasIntegrationsGrant =
    !!integrationsCap &&
    (grantedIntegrationCapabilities === '*' || grantedIntegrationCapabilities.size > 0)

  return function moduleData(from, op, args, cb) {
    // MODULE-FN-READONLY-ENFORCED-1 — inside a verb the manifest marks
    // `readOnly: true` (run on a read-only caller, moduleReadOnlyCall.js) only
    // record / schema READS pass; a write or an integration call never
    // reaches the SDK.
    const readOnlyCall = readOnlyCallOf(this)
    if (readOnlyCall && !isReadOnlyDataCall(from, op)) {
      return refuseInReadOnlyCall(readOnlyCall, 'moduleData', [from, op, args])
    }
    const entity = _bareEntity(from)
    const operation = String(op || '').toLowerCase()
    // Live-grant sync — see _syncLiveGrant above (cold-boot race + a grant
    // that changed after this bridge was built).
    _syncLiveGrant()

    // ── orgIntegration — row-addressed capability dispatch ONLY. Handled
    // BEFORE the generic ALLOWED_OPS gate below: 'call' is not one of the
    // `records` store's verbs (list/get/create/update/upsert/remove/
    // subscribe), so it would be rejected outright by that gate otherwise. ─
    if (entity === 'orgintegration') {
      if (!hasIntegrationsGrant) {
        return Promise.reject(
          new Error(`[module] entity "${from}" is not in this module's capability grant.`)
        )
      }
      if (operation !== 'call') {
        return Promise.reject(
          new Error(
            `[module] orgIntegration operation "${op}" is not permitted from a module — only "call".`
          )
        )
      }
      const idOrSlug = args && args.idOrSlug
      if (!idOrSlug || typeof idOrSlug !== 'string') {
        return Promise.reject(
          new Error(
            '[module] orgIntegration.call requires args.idOrSlug — row-addressed dispatch only (the body-addressed /call route has no module-grant escape hatch server-side).'
          )
        )
      }
      const capability = String((args && args.capability) || '').toLowerCase()
      if (
        !capability ||
        (grantedIntegrationCapabilities !== '*' && !grantedIntegrationCapabilities.has(capability))
      ) {
        return Promise.reject(
          new Error(
            `[module] integration capability "${
              (args && args.capability) || '(missing)'
            }" is not in this module's capability grant.`
          )
        )
      }
      const sdk = getSdkFn ? getSdkFn() : null
      if (!sdk || typeof sdk.execute !== 'function') {
        return Promise.reject(new Error('[module] SDK unavailable.'))
      }
      const finalArgs = {
        ...args,
        workspaceId: args.workspaceId || _rootWsId(root),
        orgId: args.orgId || _rootOrgId(root)
      }
      return sdk.execute('orgIntegration', 'call', finalArgs, cb)
    }

    // ── prefs — the CALLER's own per-module preferences (modulePrefs.js,
    // MODULE-USER-PREFS-1). No capability grant: the data is the person's own,
    // stored in their user-preferences row (server-scoped to the caller), and
    // partitioned by the active workspace and the calling module. The module
    // partition is NOT enforced: modules share one JS context, so module A can
    // read, write or remove B's prefs through B's element or context — see
    // modulePrefs.js. Handled before the generic op gate: 'set' is not a
    // records verb. ───────────────────────────────────────────────────────
    if (entity === 'prefs') {
      return modulePrefsCall.call(this, operation, args, {
        root,
        sdk: getSdkFn ? getSdkFn() : null,
        moduleIdOf
      })
    }

    // ── analyzed — read-only site analytics for a TRUSTED caller, pinned to
    // the tab's workspace and the published-projects family (see the file
    // header). Handled before the generic op gate: totals / funnel /
    // listEvents are not records verbs, and no records grant is involved.
    if (entity === 'analyzed') {
      const read = Object.prototype.hasOwnProperty.call(ANALYZED_READS, operation)
        ? ANALYZED_READS[operation]
        : null
      if (!read) {
        return _analyzedRefusal(
          `analyzed operation "${op}" is not permitted from a module — only totals, funnel and listEvents (read-only).`
        )
      }
      const moduleId = typeof moduleIdOf === 'function' ? moduleIdOf(this && this.context) : null
      const ref = moduleId ? _installedModuleRef(root, moduleId) : null
      if (!ref || ref.tier !== 'trusted') {
        return _analyzedRefusal(
          'analytics reads are served to a trusted module installed in this workspace only.'
        )
      }
      const wsId = _rootWsId(root)
      if (!wsId) return _analyzedRefusal('analytics reads need the workspace this tab shows.')
      const req = _analyzedRequest(read, args, wsId)
      if (req.error) return _analyzedRefusal(req.error)
      const sdk = getSdkFn ? getSdkFn() : null
      if (!sdk || typeof sdk.execute !== 'function') {
        return Promise.reject(new Error('[module] SDK unavailable.'))
      }
      return sdk.execute(
        'analyzed',
        read.op,
        read.op === 'listEvents'
          ? { filter: req.filter, options: req.options }
          : { filter: req.filter }
      )
    }

    // ── assistants — the site-assistant settings, for a TRUSTED caller whose
    // own ref grants capabilities.assistants (see the file header). Handled
    // before the generic op gate: these ops are not records verbs, and no
    // records grant is involved.
    if (entity === 'assistants') {
      const sdkOp = Object.prototype.hasOwnProperty.call(ASSISTANTS_OPS, operation)
        ? ASSISTANTS_OPS[operation]
        : null
      if (!sdkOp) {
        return _assistantsRefusal(
          `assistants operation "${op}" is not permitted from a module — only list, get, update, revisions, rollback, usage, resetCounter and testChat.`
        )
      }
      const moduleId = typeof moduleIdOf === 'function' ? moduleIdOf(this && this.context) : null
      const ref = moduleId ? _installedModuleRef(root, moduleId) : null
      if (!ref || ref.tier !== 'trusted') {
        return _assistantsRefusal(
          'assistant settings are served to a trusted module installed in this workspace only.'
        )
      }
      if (!ref.capabilities || ref.capabilities.assistants !== true) {
        return _assistantsRefusal('entity "assistants" is not in this module\'s capability grant.')
      }
      const sdk = getSdkFn ? getSdkFn() : null
      if (!sdk || typeof sdk.execute !== 'function') {
        return Promise.reject(new Error('[module] SDK unavailable.'))
      }
      const a = args && typeof args === 'object' && !Array.isArray(args) ? { ...args } : {}
      return sdk.execute('assistants', sdkOp, a, cb)
    }

    // ── growth — the sign-up funnel and the accounts, read-only, for a
    // TRUSTED `system` catalog app (see the file header). Handled before the
    // generic op gate: funnel / accounts are not records verbs, and no records
    // grant is involved.
    if (entity === 'growth') {
      const read = Object.prototype.hasOwnProperty.call(GROWTH_READS, operation)
        ? GROWTH_READS[operation]
        : null
      if (!read) {
        return _growthRefusal(
          `growth operation "${op}" is not permitted from a module — only funnel and accounts (read-only).`
        )
      }
      const moduleId = typeof moduleIdOf === 'function' ? moduleIdOf(this && this.context) : null
      const ref = moduleId ? _installedModuleRef(root, moduleId) : null
      if (!ref || ref.tier !== 'trusted') {
        return _growthRefusal(
          'growth reads are served to a trusted module installed in this workspace only.'
        )
      }
      if (String(ref.owner || '').toLowerCase() !== GROWTH_CATALOG_OWNER) {
        return _growthRefusal('growth reads are served to a Symbols catalog app only.')
      }
      const req = _growthRequest(read, args)
      if (req.error) return _growthRefusal(req.error)
      const sdk = getSdkFn ? getSdkFn() : null
      if (!sdk || typeof sdk.execute !== 'function') {
        return Promise.reject(new Error('[module] SDK unavailable.'))
      }
      // An SDK without GrowthService (before its release) THROWS "Unknown
      // entity: 'growth'" synchronously from execute; the app reads that
      // rejection as "waiting for the data service". Always a promise.
      try {
        return Promise.resolve(sdk.execute('growth', read.op, req.args))
      } catch (err) {
        return Promise.reject(err)
      }
    }

    // ── recordCollections / fieldDefs — read-only schema metadata. Also
    // handled before the generic op gate: list/get ARE in ALLOWED_OPS, but
    // gating them here keeps both entities' checks (grant + op-allowlist)
    // together in one place instead of split across two gates. ───────────
    if (entity === 'recordcollections' || entity === 'fielddefs') {
      // Live read (not the build-time isRecordsCapable const) — the grant
      // may have synced since build; see _syncLiveGrant.
      if (recordCollections.size === 0) {
        return Promise.reject(
          new Error(`[module] entity "${from}" is not in this module's capability grant.`)
        )
      }
      if (!READONLY_META_OPS.has(operation)) {
        return Promise.reject(
          new Error(
            `[module] "${from}" operation "${op}" is not permitted from a module — only list/get.`
          )
        )
      }
      const sdk = getSdkFn ? getSdkFn() : null
      if (!sdk || typeof sdk.execute !== 'function') {
        return Promise.reject(new Error('[module] SDK unavailable.'))
      }
      const service = entity === 'recordcollections' ? 'recordCollections' : 'fieldDefs'
      const finalArgs = { ...args, workspaceId: (args && args.workspaceId) || _rootWsId(root) }
      return sdk.execute(service, operation, finalArgs, cb)
    }

    if (!ALLOWED_OPS.has(operation)) {
      return Promise.reject(new Error(`[module] operation "${op}" is not permitted.`))
    }

    // v1 module grant covers the generic record store only — client-owned
    // integration data flows through its own server-enforced dispatch.
    if (entity !== 'records') {
      return Promise.reject(
        new Error(`[module] entity "${from}" is not in this module's capability grant.`)
      )
    }
    const collection = String((args && args.collection) || '').toLowerCase()
    if (!collection || !recordCollections.has(collection)) {
      return Promise.reject(
        new Error(
          `[module] records collection "${
            collection || '(missing)'
          }" is not in this module's capability grant.`
        )
      )
    }
    if (!recordOps.has(operation)) {
      return Promise.reject(
        new Error(`[module] records operation "${op}" is not in this module's capability grant.`)
      )
    }

    const sdk = getSdkFn ? getSdkFn() : null
    if (!sdk || typeof sdk.execute !== 'function') {
      return Promise.reject(new Error('[module] SDK unavailable.'))
    }
    const finalArgs = { ...args, collection }
    const invalidateCollectionReads = () => {
      const marker = `"collection":${JSON.stringify(collection)}`
      for (const k of _readCache.keys()) {
        if (k.includes('|' + entity + '|') && k.includes(marker)) _readCache.delete(k)
      }
    }
    // Reads dedupe (see _readCache above); callback-style calls bypass — the
    // cb contract is per-invocation. Writes clear the collection's window.
    if ((operation === 'list' || operation === 'get') && !cb) {
      const key = _readKey(root, entity, operation, finalArgs)
      if (key) {
        const hit = _readCache.get(key)
        if (hit && Date.now() - hit.at < _READ_TTL_MS) return hit.promise
        const promise = sdk.execute(`workspaceProject.${entity}`, operation, finalArgs)
        _readCache.set(key, { at: Date.now(), promise })
        // A failed read must not poison the window — retries re-issue.
        promise.catch(() => _readCache.delete(key))
        _sweepReadCache()
        return promise
      }
    } else if (operation === 'subscribe') {
      // A FOREIGN write can never clear this client's read window — the only
      // signal this client ever gets is the records.change/records.open hint
      // delivered right here. If that hint does not invalidate the dedupe
      // cache, the subscriber's follow-up re-read within _READ_TTL_MS is
      // served the PRE-change row and the change is swallowed silently, with
      // no retry (the hint is already consumed). Root-caused live on dev
      // 2026-08-14 against the shipped excalidraw board: records.change
      // always ARRIVED (~0.1s after the foreign PATCH), but the board's
      // re-read converged only when its previous get was >5s old — across 13
      // measured runs the miss/converge boundary sat exactly on the 5000ms
      // TTL (miss at a 4949ms-old cache entry, converge at 5086ms). So:
      // invalidate the collection's window BEFORE delivering each hint, so
      // the subscriber's re-read always reaches the server.
      if (typeof cb === 'function') {
        const deliver = cb
        cb = (evt) => {
          if (evt && (evt.type === 'records.change' || evt.type === 'records.open')) {
            invalidateCollectionReads()
          }
          return deliver(evt)
        }
      }
    } else {
      invalidateCollectionReads()
    }
    // Force the workspace-scoped namespace — the hard wall (server-enforced too).
    return sdk.execute(`workspaceProject.${entity}`, operation, finalArgs, cb)
  }
}
